import { Link, useSearchParams } from 'react-router-dom';
import SearchableSelect from '../SearchableSelect';
import { InfoDot } from '../hiring/HiringForm';
import { InlineLoader } from '../loading';
import { FieldLabel } from './bannerFormBits';
import type { FillForm } from './useFillForm';

/** The picker's two headings: what opening the form asks of the person. */
const NEEDS_DAY = 'Needs a started day';
const BANNER_ONLY = 'Banner-only';

/** Fill a form: which of the project's forms. */
export function FillFormFields({ form }: { form: FillForm }) {
  const { forms, error, retry, picked, pick } = form;
  const [params] = useSearchParams();
  const makeForms = () => {
    const next = new URLSearchParams(params);
    next.set('forms', '1');
    return { search: `?${next.toString()}` };
  };
  if (error) {
    return (
      <p className="text-xs text-red-600">
        {error}{' '}
        <button type="button" onClick={retry} className="font-medium text-primary-600 underline hover:text-primary-700">
          Try again
        </button>
      </p>
    );
  }
  if (!forms) return <InlineLoader label="Loading forms…" />;
  return (
    <div>
      <FieldLabel text="Form" htmlFor="fill-form-task" hint="Any active form from Master Data › Tasks of this project, or a banner-only form.">
        <Link to={makeForms()} className="flex-none text-xs font-medium text-primary-600 hover:text-primary-700">
          Banner-only forms
        </Link>
      </FieldLabel>
      <SearchableSelect
        id="fill-form-task"
        value={picked?.id ?? ''}
        onChange={pick}
        options={[...forms]
          .sort((a, b) => Number(a.bannerOnly === true) - Number(b.bannerOnly === true))
          .map((f) => ({ value: f.id, label: f.name, group: f.bannerOnly ? BANNER_ONLY : NEEDS_DAY }))}
        placeholder="Pick a form…"
        searchPlaceholder="Search forms…"
        allowClear={false}
        emptyText={forms.length === 0 ? 'This project has no forms' : 'No matches'}
      />
      {picked && (
        <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
          {picked.bannerOnly ? (
            <>
              <span>Banner-only: opens from this banner, with no Start Day.</span>
              <InfoDot text="It is on nobody's Tasks list and never holds up End Day. An older app cannot open it: their banner asks them to update." />
            </>
          ) : (
            <>
              <span>Needs a started day, and Tasks on for their role.</span>
              <InfoDot text="The button opens it only once they have started their day, as on their Tasks list. A role with Tasks off is told it is not enabled: send those people a banner-only form." />
            </>
          )}
        </p>
      )}
      {/* The server's words, both of them: the line, and the whole of it behind the (i). */}
      {picked?.warningShort && (
        <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700">
          <span>{picked.warningShort}</span>
          {picked.warning && <InfoDot text={picked.warning} />}
        </p>
      )}
    </div>
  );
}
